import { Link } from "react-router-dom";
import { useGetFleetUnitDetailQuery } from "../../../redux/services/fleet/fleetApi";
import { Spinner } from "../../../shared";

/**
 * Fleet hub tab on equipment show — legs using this unit and activity feed.
 *
 * @param {{ equipmentId: string }} props
 */
export function EquipmentFleetHub({ equipmentId }) {
  const { data, isLoading, isError } = useGetFleetUnitDetailQuery(
    { id: equipmentId },
    { skip: !equipmentId }
  );

  const detail = data?.data || data;

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (isError || !detail?.unit) {
    return (
      <p className="text-sm text-gray-500 py-4">Unable to load fleet hub data.</p>
    );
  }

  const legs = [
    ...(detail.legs?.asPowerUnit || []).map((leg) => ({
      ...leg,
      role: "Power unit",
    })),
    ...(detail.legs?.asTrailer || []).map((leg) => ({
      ...leg,
      role: "Trailer",
    })),
  ];

  return (
    <div className="space-y-6 py-2">
      <section>
        <h3 className="text-sm font-semibold text-gray-800 mb-2">
          Legs using this unit
        </h3>
        {legs.length === 0 ? (
          <p className="text-sm text-gray-500">No legs assigned to this unit yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {legs.map((leg) => (
              <li key={`${leg._id}-${leg.role}`} className="px-3 py-2 text-sm">
                <div className="flex justify-between gap-2">
                  <span>
                    {leg.role} · Order #{leg.orderNumber || leg.loadNumber || "—"}
                  </span>
                  <span className="text-gray-500">{leg.status}</span>
                </div>
                {leg.jobId?._id && (
                  <Link
                    className="text-primary-600 text-xs hover:underline"
                    to={`/jobs/${leg.jobId._id}`}
                  >
                    View job
                  </Link>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Activity</h3>
        {(detail.activity || []).length === 0 ? (
          <p className="text-sm text-gray-500">No fleet activity recorded yet.</p>
        ) : (
          <ul className="space-y-2">
            {detail.activity.map((event) => (
              <li
                key={event._id}
                className="text-sm border border-gray-100 rounded px-3 py-2"
              >
                <div className="font-medium text-gray-800">{event.type}</div>
                <div className="text-gray-500 text-xs">
                  {event.summary || "—"} ·{" "}
                  {event.observedAt
                    ? new Date(event.observedAt).toLocaleString()
                    : ""}
                  {event.source ? ` · ${event.source}` : ""}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
